import { useState, useEffect, useCallback } from 'react'
import { GoalForm } from '@/components/forms/GoalForm'

interface Goal {
  id: number
  name: string
  target_amount: number
  current_amount: number
  inflation_rate: number
  target_date: string | null
  progress: number
  remaining: number
  created_at: string
}

export function Goals() {
  const [goals, setGoals] = useState<Goal[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editGoal, setEditGoal] = useState<Goal | null>(null)

  const fetchGoals = useCallback(async () => {
    try {
      const response = await fetch('/api/goals')
      if (response.ok) {
        const data = (await response.json()) as { goals: Goal[] }
        setGoals(data.goals || [])
      }
    } catch (err) {
      console.error('Error fetching goals:', err)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchGoals()
  }, [fetchGoals])

  function handleEdit(goal: Goal) {
    setEditGoal(goal)
    setIsFormOpen(true)
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this goal?')) return

    try {
      const response = await fetch(`/api/goals/${id}`, { method: 'DELETE' })
      if (response.ok) {
        setGoals((prev) => prev.filter((g) => g.id !== id))
      }
    } catch (err) {
      console.error('Error deleting goal:', err)
    }
  }

  function handleFormClose() {
    setIsFormOpen(false)
    setEditGoal(null)
  }

  function handleFormSuccess() {
    fetchGoals()
  }

  function formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount)
  }

  function getProgressColor(progress: number): string {
    if (progress < 25) return 'text-danger'
    if (progress < 50) return 'text-warning'
    if (progress < 75) return 'text-sky'
    return 'text-success'
  }

  function getProgressBgColor(progress: number): string {
    if (progress < 25) return 'from-danger to-warning'
    if (progress < 50) return 'from-warning to-sky'
    if (progress < 75) return 'from-sky to-sage'
    return 'from-sage to-success'
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-forest-dark">Goals</h1>
          <p className="text-sage mt-1">Track your savings goals</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="px-4 py-2 bg-forest text-white rounded-lg text-sm hover:bg-forest-dark transition-colors"
        >
          + Create Goal
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12 text-sage">Loading goals...</div>
      )}

      {/* Empty State */}
      {!isLoading && goals.length === 0 && (
        <div className="bg-white rounded-xl shadow-sm p-6 border border-stone/10">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎯</div>
            <h3 className="text-xl font-semibold text-charcoal">No goals yet</h3>
            <p className="text-sage mt-2 max-w-md mx-auto">
              Create your first savings goal to start tracking your progress.
              Goals help you visualize and achieve your financial targets.
            </p>
            <button
              onClick={() => setIsFormOpen(true)}
              className="mt-6 px-6 py-3 bg-forest text-white rounded-lg hover:bg-forest-dark transition-colors"
            >
              Create Your First Goal
            </button>
          </div>
        </div>
      )}

      {/* Goals List */}
      {!isLoading && goals.length > 0 && (
        <div className="space-y-4">
          {goals.map((goal) => (
            <div
              key={goal.id}
              className="bg-white rounded-xl shadow-sm p-6 border border-stone/10"
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-semibold text-lg text-charcoal">
                    {goal.name}
                  </h3>
                  {goal.target_date && (
                    <p className="text-sm text-sage">
                      Target:{' '}
                      {new Date(goal.target_date).toLocaleDateString('en-US', {
                        month: 'long',
                        year: 'numeric'
                      })}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`text-lg font-bold ${getProgressColor(goal.progress)}`}
                  >
                    {goal.progress}%
                  </span>
                  <button
                    onClick={() => handleEdit(goal)}
                    className="text-sage hover:text-charcoal transition-colors"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDelete(goal.id)}
                    className="text-sage hover:text-danger transition-colors"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-charcoal tabular-nums">
                    {formatCurrency(goal.current_amount)}
                  </span>
                  <span className="text-sage tabular-nums">
                    {formatCurrency(goal.target_amount)}
                  </span>
                </div>
                <div className="h-4 bg-cream rounded-full overflow-hidden">
                  <div
                    className={`h-full bg-gradient-to-r ${getProgressBgColor(goal.progress)} rounded-full transition-all duration-500`}
                    style={{ width: `${goal.progress}%` }}
                  />
                </div>
                {/* Milestone markers */}
                <div className="flex justify-between mt-2 text-xs text-stone">
                  <span className={goal.progress >= 25 ? 'text-success' : ''}>
                    25%
                  </span>
                  <span className={goal.progress >= 50 ? 'text-success' : ''}>
                    50%
                  </span>
                  <span className={goal.progress >= 75 ? 'text-success' : ''}>
                    75%
                  </span>
                  <span className={goal.progress >= 100 ? 'text-success' : ''}>
                    100%
                  </span>
                </div>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-stone/10">
                <div>
                  <p className="text-xs text-sage">Remaining</p>
                  <p className="text-lg font-semibold text-charcoal tabular-nums">
                    {formatCurrency(goal.remaining)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-sage">Inflation Rate</p>
                  <p className="text-lg font-semibold text-charcoal">
                    {(goal.inflation_rate * 100).toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Goal Form Modal */}
      <GoalForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editGoal={editGoal}
      />
    </div>
  )
}
